/**
 * Settings → Numbering route.
 *
 * Loads `numbering.get`, hydrates the form, and on Save runs one
 * `numbering.update` mutation per dirty card. The "Continue from
 * existing series" affordance opens its own dialog → `numbering.setNextSeq`.
 *
 * Save model mirrors `/settings/company`: dirty tracking against a
 * baseline; SaveBar shows idle / dirty / saving / saved. After a save
 * completes, `numbering.get` is invalidated and the baseline rehydrates.
 *
 * @see prototype/settings/settings-numbering.html — visual reference
 */
import { useState, useEffect, useMemo, useCallback } from "react";
import { createFileRoute, notFound } from "@tanstack/react-router";
import { Alert } from "@flint/ui";
import { hasPermission } from "@flint/api-contract";
import { trpc } from "../../../../lib/trpc.ts";
import { formatRelativeTime } from "../../../../lib/format.ts";
import { isTrpcCauseShape } from "../../../../lib/trpc-errors.ts";
import { meQueryOptions } from "../../../../lib/session.ts";
import { WorkspaceShell } from "../../../../features/workspace/index.ts";
import { SettingsShell, SaveBar, type SaveBarState } from "../../../../features/settings/index.ts";
import {
  NumberingForm,
  SetNextSeqDialog,
  rowsToState,
  anyCardDirty,
  dirtyDocumentTypes,
  formatPreview,
  type NumberingFormState,
} from "../../../../features/settings/numbering/index.ts";

export const Route = createFileRoute("/(workspace)/settings/numbering/")({
  // URL-level `settings.manage` gate — see /settings/company/index.tsx for rationale.
  beforeLoad: async ({ context }) => {
    const me = await context.queryClient.ensureQueryData(meQueryOptions).catch(() => null);
    const perms = me?.company?.permissions ?? [];
    if (!hasPermission(perms, "settings.manage")) {
      throw notFound();
    }
  },
  component: NumberingSettingsPage,
});

type DocType = "tax-invoice" | "credit-note" | "receipt" | "vendor-payment";

const isDocType = (v: string): v is DocType =>
  v === "tax-invoice" || v === "credit-note" || v === "receipt" || v === "vendor-payment";

function NumberingSettingsPage() {
  const utils = trpc.useUtils();
  const numberingQuery = trpc.numbering.get.useQuery({});

  const [baseline, setBaseline] = useState<NumberingFormState>({});
  const [state, setState] = useState<NumberingFormState>({});
  const [savedAt, setSavedAt] = useState<Date | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [serverError, setServerError] = useState<string | null>(null);

  // Hydrate when wire data arrives or refetches. Don't clobber the
  // operator's in-flight edits on a background refetch.
  useEffect(() => {
    if (!numberingQuery.data) return;
    const next = rowsToState(numberingQuery.data.rows);
    setBaseline(next);
    setState((current) => (anyCardDirty(current, baseline) ? current : next));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [numberingQuery.data]);

  // ── Mutations ──────────────────────────────────────────────────
  const update = trpc.numbering.update.useMutation();
  const setNextSeq = trpc.numbering.setNextSeq.useMutation();

  const dirty = anyCardDirty(state, baseline);

  const saveBarState: SaveBarState = update.isPending
    ? "saving"
    : dirty
      ? "dirty"
      : savedAt
        ? "saved"
        : "idle";

  const statusText = useMemo(() => {
    if (update.isPending) return "Saving…";
    if (dirty) return "Unsaved changes — review before saving";
    if (savedAt) return `All changes saved · ${formatRelativeTime(savedAt)}`;
    return "No changes yet";
  }, [update.isPending, dirty, savedAt]);

  // ── Save (multi-call) ──────────────────────────────────────────
  //
  // We fire one mutation per dirty card sequentially so a partial
  // failure (e.g., invalid-prefix on card 2) doesn't lose the success
  // of card 1. Field errors are tagged with the doc type so the form
  // can surface them on the right row.
  const onSave = useCallback(async () => {
    setFieldErrors({});
    setServerError(null);
    const dirtyTypes = dirtyDocumentTypes(state, baseline);
    if (dirtyTypes.length === 0) return;

    const nextErrors: Record<string, string> = {};
    for (const dt of dirtyTypes) {
      const card = state[dt];
      const base = baseline[dt];
      if (!card || !base || !isDocType(dt)) continue;
      const payload: {
        documentType: DocType;
        prefix?: string;
        padding?: 3 | 4 | 5 | 6;
      } = { documentType: dt };
      if (card.prefix !== base.prefix) payload.prefix = card.prefix;
      if (card.padding !== base.padding) payload.padding = card.padding;

      try {
        await update.mutateAsync(payload);
      } catch (err: unknown) {
        if (isTrpcCauseShape(err, { reason: "invalid-prefix" })) {
          nextErrors[`${dt}:prefix`] = "Use 1–8 uppercase letters, digits, or dashes.";
        } else if (isTrpcCauseShape(err, { reason: "invalid-padding" })) {
          nextErrors[`${dt}:padding`] = "Padding must be 3, 4, 5, or 6.";
        } else if (err instanceof Error) {
          setServerError(err.message);
        }
      }
    }

    if (Object.keys(nextErrors).length > 0) setFieldErrors(nextErrors);
    await utils.numbering.get.invalidate();
    setSavedAt(new Date());
  }, [state, baseline, update, utils.numbering.get]);

  const onDiscard = useCallback(() => {
    setState(baseline);
    setFieldErrors({});
    setServerError(null);
  }, [baseline]);

  // ── Continue-from-existing-series dialog ───────────────────────
  const [setSeqDocType, setSetSeqDocType] = useState<string | null>(null);
  const [setSeqError, setSetSeqError] = useState<string | null>(null);

  const openSetNextSeq = useCallback((dt: string) => {
    setSetSeqError(null);
    setSetSeqDocType(dt);
  }, []);

  const closeSetNextSeq = useCallback((open: boolean) => {
    if (!open) {
      setSetSeqDocType(null);
      setSetSeqError(null);
    }
  }, []);

  const submitSetNextSeq = useCallback(
    async (nextSeq: number) => {
      if (!setSeqDocType || !isDocType(setSeqDocType)) return;
      setSetSeqError(null);
      try {
        await setNextSeq.mutateAsync({ documentType: setSeqDocType, nextSeq });
        await utils.numbering.get.invalidate();
        setSavedAt(new Date());
        setSetSeqDocType(null);
      } catch (err: unknown) {
        if (isTrpcCauseShape(err, { reason: "next-seq-locked" })) {
          setSetSeqError(
            "Already issued in this FY — the starting number can't be reset without voiding existing documents.",
          );
        } else if (isTrpcCauseShape(err, { reason: "invalid-next-seq" })) {
          setSetSeqError("Enter a positive whole number.");
        } else if (err instanceof Error) {
          setSetSeqError(err.message);
        }
      }
    },
    [setSeqDocType, setNextSeq, utils.numbering.get],
  );

  const activeDialogCard = setSeqDocType ? state[setSeqDocType] : null;

  return (
    <WorkspaceShell breadcrumbs={[{ label: "Settings" }, { label: "Numbering" }]}>
      <SettingsShell
        activeSection="numbering"
        pageTitle="Numbering"
        pageDescription="Per-document prefixes, padding, and starting numbers. Flint issues GST-Rule-46-compliant sequences — gap-free, no-reuse, auto-resets at every FY."
      >
        {numberingQuery.isLoading ? (
          <p className="text-muted text-sm">Loading numbering…</p>
        ) : numberingQuery.error ? (
          <Alert
            intent="bad"
            title="Couldn't load numbering"
            description={numberingQuery.error.message}
          />
        ) : numberingQuery.data ? (
          <>
            <NumberingForm
              fy={numberingQuery.data.fy}
              state={state}
              baseline={baseline}
              fieldErrors={fieldErrors}
              serverError={serverError}
              onChange={setState}
              onOpenSetNextSeq={openSetNextSeq}
            />
            <SaveBar
              state={saveBarState}
              statusText={statusText}
              onDiscard={onDiscard}
              onSave={() => void onSave()}
            />
            {activeDialogCard ? (
              <SetNextSeqDialog
                open={Boolean(setSeqDocType)}
                onOpenChange={closeSetNextSeq}
                documentType={setSeqDocType!}
                documentLabel={activeDialogCard.label}
                formatPreview={(seq) =>
                  formatPreview(
                    activeDialogCard.prefix,
                    numberingQuery.data!.fy,
                    seq,
                    activeDialogCard.padding,
                  )
                }
                serverError={setSeqError}
                isPending={setNextSeq.isPending}
                onSubmit={(nextSeq) => void submitSetNextSeq(nextSeq)}
              />
            ) : null}
          </>
        ) : null}
      </SettingsShell>
    </WorkspaceShell>
  );
}
